import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { SHOP_HEADER } from 'src/app/interceptors/auth.interceptor';
import { CONSTANTS } from 'src/app/providers/constants';
import { ENVIRONMENT } from 'src/app/providers/environment';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PageReloadService } from 'src/app/services/page-reload/page-reload.service';
import { readTokenShop } from 'src/app/shared/utils/read-token-shop/read-token-shop';
import { AgentChatError, AgentChatEvent, AgentMessage } from 'src/app/types/agent-chat';

/** The header `ShopifyGuard` sends with a 401 when a new session token would be accepted. */
const RETRY_HEADER = 'x-shopify-retry-invalid-session-request';

/** Splits Server-Sent Events text into `{ type, ...data }` events; keeps an incomplete tail for the next read. */
export function parseServerSentEvents(buffer: string): { events: AgentChatEvent[]; rest: string } {
  const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: AgentChatEvent[] = [];

  for (const block of blocks) {
    let type = 'message';
    let data = '';

    for (const line of block.split('\n'))
      if (line.startsWith('event:')) type = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();

    if (!data) continue;

    try {
      events.push({ ...(JSON.parse(data) as object), type } as AgentChatEvent);
    } catch {
      // A malformed event is skipped rather than ending the reply.
    }
  }

  return { events, rest };
}

/**
 * Sends a merchant message (GOO-457). The agent's reply streams as Server-Sent Events over a POST,
 * which `HttpClient` can't read as it arrives, so this uses `fetch` and does by hand what the
 * interceptors do for every other request: attach the token and the shop, and on a 401 that
 * invites a retry, mint a new token from the Shopify session and send once more (see
 * `shopifyInterceptor`). A message stored for the team comes back as plain JSON, as one `stored`
 * event.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentChatStreamService {
  private readonly env = inject(ENVIRONMENT);
  private readonly constants = inject(CONSTANTS);
  private readonly authService = inject(AuthService);
  private readonly pageReload = inject(PageReloadService);

  /** Starts a conversation without `conversationId`. Unsubscribing stops the reply. */
  send(conversationId: string | undefined, content: string): Observable<AgentChatEvent> {
    return new Observable<AgentChatEvent>((subscriber) => {
      const abort = new AbortController();

      this.run(conversationId, content, abort.signal, (event) => subscriber.next(event))
        .then(() => subscriber.complete())
        .catch((error: unknown) => {
          if (!abort.signal.aborted) subscriber.error(error);
        });

      return () => abort.abort();
    });
  }

  private async run(
    conversationId: string | undefined,
    content: string,
    signal: AbortSignal,
    emit: (event: AgentChatEvent) => void,
  ) {
    const url = `${this.env.server}/agent/conversations/${conversationId ? `${conversationId}/` : ''}messages`;
    let response = await this.post(url, content, signal);

    if (response.status === 401 && response.headers.has(RETRY_HEADER)) {
      const token = await this.renewToken();

      // The store changed under this tab; the page is reloading.
      if (!token) return;

      response = await this.post(url, content, signal);
    }

    if (!response.ok) throw await this.toError(response);

    if (!response.headers.get('Content-Type')?.includes('text/event-stream')) {
      const stored = (await response.json()) as { conversationId: string; message: AgentMessage };

      emit({ type: 'stored', ...stored });

      return;
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();

      if (done) break;

      const parsed = parseServerSentEvents(buffer + decoder.decode(value, { stream: true }));

      buffer = parsed.rest;
      parsed.events.forEach(emit);
    }

    parseServerSentEvents(`${buffer}\n\n`).events.forEach(emit);
  }

  private post(url: string, content: string, signal: AbortSignal) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    const token = localStorage.getItem(this.constants.AUTH_TOKEN_KEY as string);
    const shop = window.shopify?.config?.shop;

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      if (shop) headers[SHOP_HEADER] = shop;
    }

    return fetch(url, { method: 'POST', headers, body: JSON.stringify({ content }), signal });
  }

  /**
   * What `shopifyInterceptor` does on the same 401: drop the stale token and mint a new one. When
   * the new token is for another store, the merchant switched store under this tab, so the page
   * reloads rather than sending to the wrong one; `null` then.
   */
  private async renewToken(): Promise<string | null> {
    const staleShop = readTokenShop(localStorage.getItem(this.constants.AUTH_TOKEN_KEY as string));

    localStorage.removeItem(this.constants.AUTH_TOKEN_KEY as string);

    if (this.authService.isAdmin) throw new AgentChatError(401, 'UNAUTHORIZED');

    const token = await firstValueFrom(this.authService.exchangeShopifyToken());
    const newShop = readTokenShop(token);

    if (staleShop && newShop && staleShop.toLowerCase() !== newShop.toLowerCase()) {
      this.pageReload.reload();

      return null;
    }

    return token;
  }

  /** The limits carry `retryAfterSeconds` in the body: CORS doesn't let a page read `Retry-After`. */
  private async toError(response: Response) {
    const body = (await response.json().catch(() => ({}))) as { message?: unknown; retryAfterSeconds?: unknown };

    return new AgentChatError(
      response.status,
      typeof body.message === 'string' ? body.message : '',
      typeof body.retryAfterSeconds === 'number' && body.retryAfterSeconds > 0 ? body.retryAfterSeconds : undefined,
    );
  }
}
